import { useState, useEffect } from "react";
import api from "../../services/api";
import {
  Users, CheckCircle2, XCircle, Trash2, Clock,
  UserCheck, Loader2, AlertCircle, Search
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

export default function InstituteParentLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | approved | rejected
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/institute/parent-links");
      setLinks(res.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAction = async (id, status) => {
    try {
      await api.put(`/admin/parent-links/${id}`, { status });
      toast.success(`Request ${status}!`);
      load();
    } catch { toast.error("Action failed"); }
  };

  

  const filtered = links
    .filter(l => filter === "all" || l.status === filter)
    .filter(l => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        l.parent_name?.toLowerCase().includes(q) ||
        l.parent_email?.toLowerCase().includes(q) ||
        l.student_name?.toLowerCase().includes(q) ||
        l.student_email?.toLowerCase().includes(q)
      );
    });

  const counts = {
    all: links.length,
    pending: links.filter(l => l.status === "pending").length,
    approved: links.filter(l => l.status === "approved").length,
    rejected: links.filter(l => l.status === "rejected").length,
  };

  const statusBadge = (status) => {
    const styles = {
      pending: "bg-amber-50 text-amber-600 dark:bg-amber-900/30",
      approved: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30",
      rejected: "bg-rose-50 text-rose-600 dark:bg-rose-900/30",
    };
    const icons = {
      pending: <Clock size={12} />,
      approved: <CheckCircle2 size={12} />,
      rejected: <XCircle size={12} />,
    };
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${styles[status]}`}>
        {icons[status]} {status}
      </span>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="text-blue-600 animate-spin" size={32} />
    </div>
  );

  return (
    <div className="space-y-8 font-['Inter',sans-serif]">
      <Toaster position="top-center" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
            <UserCheck className="text-violet-600" size={28} /> Parent Link Requests
          </h1>
          <p className="text-sm text-slate-500 mt-1">Approve or reject parent access to student progress</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 rounded-xl border border-amber-200 dark:border-amber-800">
          <Clock size={16} />
          <span className="text-sm font-black">{counts.pending} Pending</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1 gap-1">
          {["all", "pending", "approved", "rejected"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                filter === f
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              }`}>
              {f} ({counts[f]})
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search by name or email..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-500/50" />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
          <AlertCircle size={40} className="mx-auto text-slate-300 mb-4" />
          <p className="font-black text-slate-500">No link requests found</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                  <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Parent</th>
                  <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Student</th>
                  <th className="text-center px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="text-center px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Requested</th>
                  <th className="text-right px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(link => (
                  <tr key={link.id} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">{link.parent_name}</p>
                        <p className="text-[11px] text-slate-400">{link.parent_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">{link.student_name}</p>
                        <p className="text-[11px] text-slate-400">{link.student_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">{statusBadge(link.status)}</td>
                    <td className="px-4 py-4 text-center text-xs text-slate-400 font-bold">
                      {link.created_at ? new Date(link.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {link.status !== "approved" && (
                          <button onClick={() => handleAction(link.id, "approved")}
                            className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all"
                            title="Approve">
                            <CheckCircle2 size={18} />
                          </button>
                        )}
                        {link.status !== "rejected" && (
                          <button onClick={() => handleAction(link.id, "rejected")}
                            className="p-2 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all"
                            title="Reject">
                            <XCircle size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
